import { CanvasItem, Section, Element } from '../types/types';
import { getSectionDefaultProperties } from './pageElementDefaultSettings';
import { createRuntimeElementId, createStableElementClass } from './pageBuilderElementClass';

/**
 * Allowed section properties that are editable in SectionProperties UI
 * Only these properties are included in the backend payload
 */
const ALLOWED_SECTION_PROPERTIES = [
  // Container Background
  'bgColor',

  // Image Media
  'imageUrl',
  'imageExternalURL',
  'imageFileName',
  'imageFileSize',

  // Video Media
  'videoUrl',
  'videoExternalURL',
  'videoFileName',
  'videoFileSize',

  // Media Type
  'mediaType',

  // Image Overlay
  'imageOverlayColor',
  'imageOverlayOpacity'
];

/**
 * Filters section properties to only include allowed properties
 * Ensures API payload stays clean and strictly aligned with schema
 */
// export const filterSectionProperties = (settings: Record<string, any> | undefined): Record<string, any> => {
//   if (!settings) {
//     return getSectionDefaultProperties('single_column');
//   }

//   const filtered: Record<string, any> = {};

//   ALLOWED_SECTION_PROPERTIES.forEach(key => {
//     if (key in settings && settings[key] !== undefined) {
//       filtered[key] = settings[key];
//     }
//   });

//   return filtered;
// };

export const filterSectionProperties = (settings: Record<string, any> | undefined): Record<string, any> => {
  if (!settings) {
    return {};
  }

  const filtered: Record<string, any> = {};

  ALLOWED_SECTION_PROPERTIES.forEach(key => {
    if (key in settings && settings[key] !== undefined && settings[key] !== null && settings[key] !== '') {
      filtered[key] = settings[key];
    }
  });

  // Remove signed URLs before sending
  delete (filtered as any).imageSignedUrl;
  delete (filtered as any).videoSignedUrl;

  return filtered;
};

/**
 * Transforms frontend canvas items into backend payload format
 * Frontend format: Section | Element (mixed)
 * Backend format: rows array with column types, sectionPosition, and elements array
 * 
 * Each section includes:
 * - column: 1 (single-column) or 2 (multi-column)
 * - sectionPosition: Order of section on page (1, 2, 3...)
 * - properties: Section styling and settings (only allowed properties)
 * - elements: Array of elements with elementPosition and belongsTo metadata
 */
export const transformCanvasToPayload = (
  canvasItems: CanvasItem[],
    pageElementsData?: any,
    colorSettings?: { primaryColor?: string; textColor?: string; secondaryColor?: string }
  ): { rows: any[] } => {
  const rows: any[] = [];
  let sectionPosition = 1;

  canvasItems.forEach((item) => {
    // Check if item is a Section
    if ('type' in item && (item.type === 'single_column' || item.type === 'multi_column')) {
      const section = item as Section;
      const columnType = section.type === 'single_column' ? 1 : 2;

      // Section row with layout type and styling properties
      // Filter properties to only include allowed ones from SectionProperties UI
      const sectionDefaults = getSectionDefaultProperties(section.type);
      const filteredProperties = filterSectionProperties({
        ...sectionDefaults,
        ...(section.properties || {})
      });

      const row: any = {
        column: columnType,                    // 1 = single-column, 2 = multi-column
        sectionPosition: sectionPosition,      // Order on page
        settings: filteredProperties,        // Only allowed properties included
        elements: []
      };

      if (columnType === 1) {
        // Single-column: all elements belong to column 1
        let elementPosition = 1;
        row.elements = section.elements.map((el) => ({
          ...transformElementToPayload(el, pageElementsData, colorSettings),
          elementPosition: elementPosition++,
          belongsTo: 1
        }));
      } else {
        // Multi-column: track position separately per column
        let leftPosition = 1;
        let rightPosition = 1;

        if (section.columns?.left) {
          section.columns.left.forEach((el) => {
            row.elements.push({
              ...transformElementToPayload(el, pageElementsData, colorSettings),
              elementPosition: leftPosition++,
              belongsTo: 1 // Left column
            });
          });
        }

        if (section.columns?.right) {
          section.columns.right.forEach((el) => {
            row.elements.push({
              ...transformElementToPayload(el, pageElementsData, colorSettings),
              elementPosition: rightPosition++,
              belongsTo: 2 // Right column
            });
          });
        }
      }

      rows.push(row);
      sectionPosition++;
    }
    // Note: Top-level elements are deprecated, skipping
  });

  return { rows };
};

/**
 * Transform a single element to backend payload format
 * Maps element type and properties to backend field names
 */
const transformElementToPayload = (
  element: Element,
  pageElementsData?: any,
  colorSettings?: { primaryColor?: string; textColor?: string; secondaryColor?: string }
): any => {
  const payload: any = {
    accessKey: element.type,
    content: element.content || ''
  };

  // Spread properties
  // if (element.properties) {
  //   payload.settings = { ...element.properties };
  // }
  if (element.properties) {
    const { imagePreview, videoPreview, imageSignedUrl, videoSignedUrl, _changedColorKeys, _apiColorKeys, _meta: _propMeta, ...cleanProperties } = element.properties;

    // Strip color fields for specific element types unless user explicitly changed them
    const COLOR_FIELDS_BY_TYPE: Record<string, string[]> = {
      heading: ['color'],
      paragraph: ['paragraphColor'],
      separator: ['separatorColor'],
      // buttonTextColor is intentionally excluded; it should continue being sent
      button: ['buttonColor', 'buttonHoverColor'],
    };
    const changedColorKeys: string[] = Array.isArray(_changedColorKeys) ? _changedColorKeys : [];
    const apiColorKeys: string[] = Array.isArray(_apiColorKeys) ? _apiColorKeys : [];
    const colorFieldsForType = COLOR_FIELDS_BY_TYPE[element.type as string] ?? [];

    colorFieldsForType.forEach(colorKey => {
      if (!changedColorKeys.includes(colorKey) && !apiColorKeys.includes(colorKey)) {
        delete (cleanProperties as any)[colorKey];
      }
    });

    if (element.type === 'public_directory') {
      const queryPayload = cleanProperties.queryPayload ?? cleanProperties.publicDirectoryPayload ?? {
        selectedColumns: Array.isArray(cleanProperties.selectedColumns)
          ? cleanProperties.selectedColumns
          : Array.isArray(cleanProperties.selectedFields)
            ? cleanProperties.selectedFields.map((field: any) => field?.value ?? field?.field ?? field).filter(Boolean)
            : [],
        criteria: {
          logic: cleanProperties.logic === 'Any' ? 'OR' : cleanProperties.logic === 'OR' ? 'OR' : 'AND',
          conditions: Array.isArray(cleanProperties.criteria)
            ? cleanProperties.criteria.map((condition: any) => ({
              field: String(condition?.field ?? condition?.column ?? ''),
              operator: String(condition?.operator ?? condition?.condition ?? ''),
              value: condition?.value ?? ''
            }))
            : Array.isArray(cleanProperties.publicDirectoryPayload?.criteria?.conditions)
              ? cleanProperties.publicDirectoryPayload.criteria.conditions.map((condition: any) => ({
                field: String(condition?.field ?? condition?.column ?? ''),
                operator: String(condition?.operator ?? condition?.condition ?? ''),
                value: condition?.value ?? ''
              }))
              : []
        }
      };

      payload.settings = {
        htmlConfig: cleanProperties.htmlConfig ?? {
          tag: 'div',
          content: '',
          attributes: {
            class: 'public_directory-element'
          }
        },
        elementClass: cleanProperties.elementClass,
        publicDirectorySetting: {
          queryPayload,
          scope: cleanProperties.scope || '',
          name: 'public directory'
        }
      };
    } else {
      payload.settings = { ...cleanProperties };
    }
  }

  // Map specific element types to their backend field names
  switch (element.type) {
    case 'form':
      // Send only current form identity-related settings for form elements.
      // This prevents stale nested objects (e.g. settings.form.formData) from older selections.
      if (payload.settings) {
        const sanitizedFormSettings: Record<string, any> = {};

        if (element.properties?.scope) {
          sanitizedFormSettings.scope = element.properties.scope;
        }
        if (element.properties?.formId) {
          sanitizedFormSettings.formId = element.properties.formId;
        }
        if (element.properties?.formName) {
          sanitizedFormSettings.formName = element.properties.formName;
        }
        if (element.properties?.elementClass) {
          sanitizedFormSettings.elementClass = element.properties.elementClass;
        }

        payload.settings = sanitizedFormSettings;
      }

      if (element.properties?.formId) {
        payload.formId = element.properties.formId;
      }
      break;

    case 'search_query':
      if (element.properties?.searchQueryId) {
        payload.searchQueryId = element.properties.searchQueryId;
      }
      break;

    case 'heading':
    case 'paragraph':
    case 'button':
    case 'separator':
    case 'media':
    case 'image':
    case 'video':
      // These elements use their properties as-is
      break;

    case 'public_directory':
      break;

    default:
      break;
  }

  return payload;
};

/**
 * Transforms backend canvas response into frontend canvas format
 * Backend format: rows array with column, sectionPosition, elements
 * Frontend format: CanvasItem[] (Section | Element)
 */
export const transformPayloadToCanvas = (
  backendPayload: { rows: any[] },
  pageElementsData?: any
): CanvasItem[] => {
  const canvasItems: CanvasItem[] = [];

  backendPayload.rows.forEach((row, index) => {
    const columnType = row.column === 1 ? 'single_column' : 'multi_column';
    const section: Section = {
      id: `section-${Date.now()}-${index}`,
      type: columnType,
      properties: row.properties || {},
      elements: [],
      ...(columnType === 'multi_column' && {
        columns: {
          left: [],
          right: []
        }
      })
    };

    // Transform elements
    row.elements?.forEach((el: any) => {
      const element = transformPayloadToElement(el, pageElementsData);

      if (columnType === 'single_column') {
        section.elements.push(element);
      } else {
        // Multi-column: distribute by belongsTo
        if (el.belongsTo === 1) {
          (section.columns as any).left.push(element);
        } else if (el.belongsTo === 2) {
          (section.columns as any).right.push(element);
        }
      }
    });

    canvasItems.push(section);
  });

  return canvasItems;
};

/**
 * Transform a single backend element to frontend format
 */
const transformPayloadToElement = (payloadElement: any, pageElementsData?: any): Element => {
  const elementId = createRuntimeElementId(payloadElement.accessKey);
  const rawSettings = payloadElement.properties || payloadElement.settings || {};
  const publicDirectorySetting = rawSettings.publicDirectorySetting || {};

  const element: Element = {
    id: elementId,
    type: payloadElement.accessKey,
    content: payloadElement.content || '',
    properties: rawSettings
  };

  element.properties = {
    ...(element.properties || {}),
    ...(payloadElement.accessKey === 'public_directory' ? {
      scope: publicDirectorySetting.scope ?? element.properties?.scope,
      queryPayload: publicDirectorySetting.queryPayload ?? element.properties?.queryPayload,
      publicDirectoryPayload: publicDirectorySetting.queryPayload ?? element.properties?.publicDirectoryPayload,
      publicDirectorySetting: publicDirectorySetting.name || publicDirectorySetting.queryPayload || publicDirectorySetting.scope
        ? {
          name: publicDirectorySetting.name ?? 'public directory',
          scope: publicDirectorySetting.scope ?? element.properties?.scope ?? '',
          queryPayload: publicDirectorySetting.queryPayload ?? element.properties?.queryPayload ?? element.properties?.publicDirectoryPayload
        }
        : element.properties?.publicDirectorySetting
    } : {}),
    elementClass: element.properties?.elementClass || element.properties?.className || createStableElementClass(payloadElement.accessKey, payloadElement.id)
  };

  // Map backend-specific fields back to properties
  if (element.properties) {
    if (payloadElement.formId) {
      element.properties.formId = payloadElement.formId;
    }

    if (payloadElement.searchQueryId) {
      element.properties.searchQueryId = payloadElement.searchQueryId;
    }

    // Include full details if present
    if (payloadElement.formDetails) {
      element.properties.formDetails = payloadElement.formDetails;
    }

    if (payloadElement.searchQuery) {
      element.properties.searchQuery = payloadElement.searchQuery;
    }
  }

  return element;
};

/**
 * Generate a complete page payload ready for backend POST
 * 
 * Output structure:
 * {
 *   title: string,
 *   header: { ...settings },
 *   footer: { ...settings },
 *   rows: [
 *     {
 *       column: 1 | 2,                    // 1=single-column, 2=multi-column
 *       sectionPosition: number,          // Order on page (1, 2, 3...)
 *       properties: {...},                // Section-level styling (bgColor, overlay, media, etc)
 *       elements: [
 *         {
 *           accessKey: 'form' | 'image' | ... ,
 *           elementPosition: number,      // Position in section (scoped per column)
 *           belongsTo: 1 | 2,             // Column in multi-column layout (1=left, 2=right)
 *           ...element-specific fields
 *         }
 *       ]
 *     }
 *   ]
 * }
 */
export const generatePagePayload = (
  pageTitle: string,
  canvasItems: CanvasItem[],
  headerSettings?: any,
  footerSettings?: any,
    pageElementsData?: any,
    colorSettings?: { primaryColor?: string; textColor?: string; secondaryColor?: string }
) => {
  return {
    title: pageTitle,
    header: headerSettings ? { ...headerSettings } : {},
    footer: footerSettings ? { ...footerSettings } : {},
      ...transformCanvasToPayload(canvasItems, pageElementsData, colorSettings)
  };
};